import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Coffee, Heart, Users, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cafeData } from '../data/cafeData';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Family Run',
      description: 'A Calabrian family café since October 2022, bringing Italy to Wimbledon.'
    },
    {
      icon: Coffee,
      title: 'Authentic Flavours',
      description: 'The food and coffee we grew up with, prepared with traditional methods.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Where neighbours become friends and customers become family.'
    },
    {
      icon: Award,
      title: 'Fresh & Simple',
      description: 'Food made fresh, simple, and full of real Italian flavour.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 px-6 md:px-12 bg-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Our Story</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1917] mt-4 mb-6">
              From Calabria to Wimbledon
            </h1>
            <p className="text-stone-500 text-lg md:text-xl leading-relaxed">
              A family's journey of bringing authentic Italian flavours and warmth to South Wimbledon.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Logo Showcase */}
      <section className="py-16 px-6 md:px-12 bg-[#FDFBF7]" data-testid="about-logo-section">
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img 
              src={cafeData.logo} 
              alt="Cafe 110 Logo" 
              className="h-40 md:h-56 w-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="about-story">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="space-y-6">
              {cafeData.about.paragraphs.map((paragraph, index) => (
                <motion.p
                  key={index}
                  className={`text-stone-700 leading-relaxed ${
                    index === 4 ? 'text-xl font-serif italic text-[#3A4D39] text-center my-8' : 'text-base md:text-lg'
                  } ${
                    index === cafeData.about.paragraphs.length - 1 ? 'text-xl font-serif italic text-[#3A4D39] text-center mt-8' : ''
                  }`}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-white" data-testid="about-values">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">What We Stand For</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C1917] mt-4">
              Our Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="text-center p-8 border border-stone-100 hover:border-[#3A4D39]/30 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-[#3A4D39]/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-[#3A4D39]" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1C1917] mb-3">{value.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hygiene Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#FDFBF7]" data-testid="about-hygiene">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="text-[#E07A5F] text-sm font-medium uppercase tracking-widest">Food Safety</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C1917] mt-4 mb-6">
                5-Star Hygiene Rating
              </h2>
              <p className="text-stone-600 leading-relaxed mb-8">
                We take food safety seriously. Our café has been awarded the highest food hygiene rating of 5 stars by Merton Council, reflecting our commitment to maintaining the highest standards.
              </p>

              <div className="space-y-4">
                {Object.entries(cafeData.hygiene.details).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between py-3 border-b border-stone-200">
                    <span className="text-stone-600 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className="font-medium text-green-600">{value}</span>
                  </div>
                ))}
              </div>

              <p className="text-stone-400 text-sm mt-6">
                Last inspected: {cafeData.hygiene.inspectionDate}
              </p>
            </motion.div>

            <motion.div
              className="flex justify-center"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="text-center">
                <div className="w-48 h-48 rounded-full bg-green-500 flex items-center justify-center mx-auto shadow-xl" data-testid="about-hygiene-badge">
                  <span className="text-white text-8xl font-bold">{cafeData.hygiene.rating}</span>
                </div>
                <p className="text-[#1C1917] font-serif text-2xl font-bold mt-6">Food Hygiene Rating</p>
                <p className="text-stone-500 mt-2">Inspected by {cafeData.hygiene.authority} Council</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#3A4D39]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
              Come Visit Us
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              We'd love to welcome you to Cafe 110. Stop by for a coffee, grab a pastry, or enjoy a leisurely Italian lunch.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/menu" data-testid="about-cta-menu-btn">
                <Button className="bg-white text-[#3A4D39] hover:bg-white/90 px-10 py-6 text-lg font-medium">
                  View Our Menu
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="about-cta-contact-btn">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#3A4D39] px-10 py-6 text-lg">
                  Find Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
